import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import TablePagination from "@mui/material/TablePagination";
import { ClipLoader } from "react-spinners";
import ShowSnackBar from "../../components/snackBar";
import { apiCall } from "../../services/authServieces";
import "../../css/walletalert.css";

const WalletAlert = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [alertAmount, setAlertAmount] = useState(0);

  const [snackBar, setSnackBar] = useState({
    open: false,
    severity: true,
    message: "",
  });

  // Modal & form states
  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [mobileNumbers, setMobileNumbers] = useState([""]);
  const [emails, setEmails] = useState([""]);
  const [alertActive, setAlertActive] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const handleCloseSnackBar = () =>
    setSnackBar((prev) => ({ ...prev, open: false }));

  // Fetch wallet users
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = `api/transaction/getUserBalanceData?limit=${rowsPerPage}&page=${page + 1}`;
      const res = await apiCall({ method: "GET", endpoint });
      if (res.success) {
        setRows(res.data);
        setTotal(res.total_count);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

 const HandleAlertNotification = async (userId) => {
  try {
    const selected = rows.find((r) => r.user_id === userId);
    setSelectedUser(selected);

    const res = await apiCall({
      method: "GET",
      endpoint: `api/transaction/getContactDetails/${userId}`,
    });

    if (res.success) {

      setIsEditMode(true);
      setMobileNumbers(res.data.mobiles || [""]);
      setEmails(res.data.emails || [""]);
      setAlertActive(res.data.alert_active);
      setAlertAmount(res.data.alert_amount || 0);  
    } else {
      setIsEditMode(false);
      setMobileNumbers([""]);
      setEmails([""]);
      setAlertActive(false);
      setAlertAmount(0);
    }

    setOpenModal(true);
  } catch (err) {
    console.error(err);
    setIsEditMode(false);
    setMobileNumbers([""]);
    setEmails([""]);
    setAlertActive(false);
    setAlertAmount(0);
    setOpenModal(true);
  }
}

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedUser(null);
    setMobileNumbers([""]);
    setEmails([""]);
    setAlertActive(false);
    setIsEditMode(false);
  };

  // 🧩 Change handlers
  const handleMobileChange = (i, val) => {
    const updated = [...mobileNumbers];
    updated[i] = val;
    setMobileNumbers(updated);
  };

  const handleEmailChange = (i, val) => {
    const updated = [...emails];
    updated[i] = val;
    setEmails(updated);
  };

  // 🧩 Add / Remove field
  const addMobile = () => {
    if (mobileNumbers.length >= 5)
      return showError("Maximum 5 mobile numbers allowed");
    setMobileNumbers([...mobileNumbers, ""]);
  };
  const addEmail = () => {
    if (emails.length >= 5)
      return showError("Maximum 5 emails allowed");
    setEmails([...emails, ""]);
  };

  const removeMobile = (i) => {
    const updated = mobileNumbers.filter((_, idx) => idx !== i);
    setMobileNumbers(updated.length ? updated : [""]);
  };

  const removeEmail = (i) => {
    const updated = emails.filter((_, idx) => idx !== i);
    setEmails(updated.length ? updated : [""]);
  };

  const showError = (msg) =>
    setSnackBar({ open: true, severity: false, message: msg });

  // 🧠 VALIDATION
  const validateBeforeSave = () => {
    const validMobiles = mobileNumbers.filter((num) => num.trim() !== "");
    const validEmails = emails.filter((em) => em.trim() !== "");

    if (alertActive && validMobiles.length === 0 && validEmails.length === 0) {
      showError("Please add at least one mobile or email if alert is active.");
      return false;
    }
    if (alertActive && (alertAmount <= 0 || isNaN(alertAmount))) {
  showError("Please enter a valid alert amount.");
  return false;
}

    // validate formats
    for (const num of validMobiles) {
      if (!/^[6-9]\d{9}$/.test(num))
        return showError("Invalid mobile number format.");
    }
    for (const em of validEmails) {
      if (!/\S+@\S+\.\S+/.test(em))
        return showError("Invalid email format.");
    }

    return true;
  };

const handleSaveData = async () => {
  if (!validateBeforeSave()) return;

  const payload = {
    userId: selectedUser?.user_id,
    mobiles: mobileNumbers.filter((x) => x.trim() !== ""),
    emails: emails.filter((x) => x.trim() !== ""),
    alert_active: alertActive,
    alert_amount: alertAmount, // <-- add here
  };

  try {
    const endpoint = isEditMode
      ? `api/transaction/updateContactDetails/${selectedUser.user_id}`
      : `api/transaction/addContactDetails/${selectedUser.user_id}`;

    const res = await apiCall({ method: "POST", endpoint, payload: payload });
    if (res.success) {
      setSnackBar({
        open: true,
        severity: true,
        message: isEditMode ? "Updated successfully!" : "Saved successfully!",
      });
      handleCloseModal();
      fetchData(); // Optional: refresh table data after save
    } else showError("Failed to save data.");
  } catch (err) {
    console.error(err);
    showError("Error saving data.");
  }
};
  // 🗑️ DELETE from backend
  const handleDeleteContact = async () => {
    try {
      const res = await apiCall({
        method: "DELETE",
        endpoint: `api/transaction/deleteContactDetails/${selectedUser?.user_id}`,
      });

      if (res.success) {
        setSnackBar({
          open: true,
          severity: true,
          message: "Contact details deleted successfully!",
        });
        handleCloseModal();
      } else showError("Error deleting data.");
    } catch (err) {
      console.error(err);
      showError("Error deleting data.");
    }
  };

  return (
    <>
      <div className="Mis_report_contian">
        <h4 className="Head_title" style={{ textAlign: "center" }}>
          Wallet Alert
        </h4>

        {/* Table */}
        <div className="Mis_report_Card122">
          <div className="table_contain1 tMisReport">
            <table className="Table" id="Table">
              <thead>
                <tr>
                  <th>Dealer Code</th>
                  <th>Dealer Name</th>
                  <th>Mobile Number</th>
                  <th>Wallet Balance</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: "center" }}>
                      No data
                    </td>
                  </tr>
                ) : (
                  rows.map((data) => (
                    <tr key={data.user_id}>
                      <td>{data.user_id}</td>
                      <td>{data.user_name}</td>
                      <td>{data.user_phone}</td>
                      <td>{Number(data.balance).toFixed(3)}</td>
                      <td>
                        <button
                          onClick={() => HandleAlertNotification(data.user_id)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>

      {/* MODAL */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
          }}
        >
          <h2>{isEditMode ? "Edit Contacts" : "Add Contacts"}</h2>
          <p>User Id: {selectedUser?.user_id}</p>

          {/* Alert Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={alertActive}
                onChange={(e) => setAlertActive(e.target.checked)}
              />
            }
            label={alertActive ? "Alert Active" : "Alert Inactive"}
            sx={{ mb: 2 }}
          />

          <TextField
  label="Alert Amount"
  type="number"
  value={alertAmount}
  onChange={(e) => setAlertAmount(Number(e.target.value))}
  fullWidth
  sx={{ mb: 2 }}
/>


          {/* Mobiles */}
          <h4>Mobile Numbers</h4>
          {mobileNumbers.map((num, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <TextField
                label={`Mobile ${i + 1}`}
                value={num}
                onChange={(e) => handleMobileChange(i, e.target.value)}
                fullWidth
              />
              <IconButton color="error" onClick={() => removeMobile(i)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          {mobileNumbers.length < 5 && (
            <Button startIcon={<AddIcon />} onClick={addMobile} variant="outlined">
              Add Mobile
            </Button>
          )}

          {/* Emails */}
          <h4 style={{ marginTop: "1rem" }}>Emails</h4>
          {emails.map((em, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <TextField
                label={`Email ${i + 1}`}
                value={em}
                onChange={(e) => handleEmailChange(i, e.target.value)}
                fullWidth
              />
              <IconButton color="error" onClick={() => removeEmail(i)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          {emails.length < 5 && (
            <Button startIcon={<AddIcon />} onClick={addEmail} variant="outlined">
              Add Email
            </Button>
          )}

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
            {isEditMode && (
              <Button color="error" onClick={handleDeleteContact}>
                Delete
              </Button>
            )}
            <Box>
              <Button onClick={handleCloseModal} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSaveData}>
                {isEditMode ? "Update" : "Save"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <ShowSnackBar
        open={snackBar.open}
        severity={snackBar.severity}
        message={snackBar.message}
        onClose={handleCloseSnackBar}
      />

      {loading && (
        <div className="spinnerOverlay">
          <ClipLoader loading={loading} size={50} />
        </div>
      )}
    </>
  );
};

export default WalletAlert;
